import i18n from "@/plugins/i18n";
import { AddressDto } from "@/dtos/addressDto";
import { PriceListDto } from "./../dtos/priceListDto";
import { PowderDto } from "./../dtos/powderDto";
import { PriceDto } from "./../dtos/priceDto";
import { CartItem } from "@/dtos/cartItem";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import { ManufacturingOfferDto } from "@/dtos/manufacturingOfferDto";
import moment from "moment";
import { SurfaceColorDto } from "@/dtos/surfaceColorDto";
import { PriceModel, getAllPriceModels } from "@/dtos/priceModel";
import { OrderDiscountDto } from "@/dtos/orderDiscountDto";
import { MwstDto } from "@/dtos/mwstDto";
import { WerkstoffDto } from "@/dtos/werkstoffDto";
import { MaterialDto } from "@/dtos/materialDto";
import { PowderFinish, PowderStructure } from "@/dtos/treatmentTypes";
import { FertigerAddressDto } from "@/dtos/fertigerAddressDto";

export default {
	customerId(state: any) {
		if (state.customer != null) {
			return state.customer.customerId;
		}
		return 0;
	},
	articleById: (state: any) => (articleId: number) => {
		return state.articles.find((a: CustomerArticleDto) => a.articleId === articleId);
	},

	subarticlesByParentId: (state: any, getters: any) => (articleId: number) => {
		const article: CustomerArticleDto = getters.articleById(articleId);
		if (!article || !article.subArticleIds?.length) return [];
		const subarticles = state.articles.filter((a: CustomerArticleDto) =>
			article.subArticleIds.includes(a.articleId)
		);
		return subarticles;
	},

	quantityOfItemInCart: (state: any) => (articleId: number) => {
		var currentCartItem: CartItem = state.currentCart.cartItems.find(
			(item: CartItem) => item.articleId == articleId
		);
		return currentCartItem != undefined ? currentCartItem.quantity : 0;
	},
	totalOfAllItemsInCart(state: any, getters: any) {
		if (state.offer) {
			return state.offer.totalOfAllArticlesInCart;
		}
		return 0.0;
	},
	isCartValid(state: any) {
		if (!state.currentCart?.cartItems?.length) {
			return false;
		}

		// Collect all the article ids from the cart items
		let articleIds = new Set<number>(
			state.currentCart.cartItems.map((ci: CustomerArticleDto) => ci.articleId)
		);

		// Add potential subarticle ids to the list
		state.articles.forEach((article: CustomerArticleDto) => {
			if (articleIds.has(article.articleId) && article.subArticleIds?.length) {
				article.subArticleIds.forEach((subId: number) => articleIds.add(subId));
			}
		});

		// Validate the collected articles
		const isInvalid = [...articleIds].some((articleId) => {
			let article = state.articles.find((a: CustomerArticleDto) => a.articleId === articleId);
			if (!article) return false;
			if (article.isAssembly) {
				return article.calculating || article.migrated || article.locked;
			} else {
				return (
					article.calculating ||
					article.migrated ||
					article.locked ||
					(article.state !== 0 && article.state !== 1)
				);
			}
		});

		return !isInvalid;
	},
	selectedManufacturer(state: any) {
		return state.offer.manufacturingOffers.find(
			(manufacturer: ManufacturingOfferDto) =>
				manufacturer.manufacturerId == state.offer.selectedManufacturerId
		);
	},
	/** True if at least one manufacturer of the customer's region makes manual offers. */
	hasManualOfferManufacturer(state: any) {
		const manualOfferFlag = 2; // OfferType.ManualOffer
		return (state.manufacturerAddresses as FertigerAddressDto[]).some(
			(address) => (address.supportedOfferTypes & manualOfferFlag) !== 0
		);
	},
	amountOfItemsInCart(state: any) {
		return state.currentCart.cartItems.length;
	},
	waehrung(state: any) {
		return state.customer.currency;
	},
	invoiceCharge(state: any) {
		if (state.offer === null) return 0.0;
		return state.currentCart.invoiceAddress.isBilledDigitally ? 0.0 : 2.0;
	},
	mwstRatio: (state: any) => (date: Date) => {
		return state.customer.mwstRates
			.filter((r: MwstDto) => {
				let rateDate = new Date(r.validFrom);
				let givenDate = new Date(date);
				return rateDate.setHours(0, 0, 0, 0) <= givenDate.setHours(0, 0, 0, 0);
			})
			.sort((a: MwstDto, b: MwstDto) => {
				let aDate = new Date(a.validFrom);
				let bDate = new Date(b.validFrom);
				return bDate.getTime() - aDate.getTime();
			})[0].rate;
	},
	deliveryCost(state: any, getters: any) {
		if (state.offer === null) return 0.0;
		if (state.offer.deliveryChoice === "delivery") {
			return getters.selectedManufacturer.deliveryCost;
		}
		return 0.0;
	},
	orderDiscount: (state: any) => (priceModel: PriceModel) => {
		if (state.offer !== null && state.offer !== undefined) {
			var orderDiscount = state.offer.orderDiscounts.filter(
				(d: OrderDiscountDto) => d.priceModel === priceModel
			)[0];
			return orderDiscount;
		}
		return null;
	},
	costMwst: (state: any, getters: any) => (priceModel: PriceModel, date: Date) => {
		let mwst =
			(parseFloat(getters.totalOfAllItemsInCart) +
				parseFloat(getters.orderDiscount(priceModel).discount) +
				parseFloat(getters.deliveryCost) +
				parseFloat(getters.invoiceCharge)) *
			((getters.mwstRatio(date) as number) / 100);
		mwst = Math.round(mwst * 100) / 100;
		return mwst;
	},
	nettoOffer: (state: any, getters: any) => (priceModel: PriceModel) => {
		return (
			parseFloat(getters.totalOfAllItemsInCart) +
			parseFloat(getters.orderDiscount(priceModel).discount) +
			parseFloat(getters.deliveryCost) +
			parseFloat(getters.invoiceCharge)
		);
	},
	totalOffer: (state: any, getters: any) => (priceModel: PriceModel, date: Date) => {
		return (
			parseFloat(getters.totalOfAllItemsInCart) +
			parseFloat(getters.orderDiscount(priceModel).discount) +
			parseFloat(getters.deliveryCost) +
			parseFloat(getters.invoiceCharge) +
			parseFloat(getters.costMwst(priceModel, date))
		);
	},
	importingArticles(state: any) {
		return state.importingArticles;
	},
	temporaryImportId(state: any) {
		return state.temporaryImportId;
	},
	getThumbnailUrl: (state: any) => (articleId: number, lastChange: any) => {
		if (lastChange == undefined) {
			lastChange = moment().toJSON();
		}
		var token = state.customer.customerId * articleId * 1234;
		var tokenAsString = token.toString();

		return (
			process.env.VUE_APP_erpUrl +
			`article/getthumbnail?id=${articleId}&time=${lastChange}&token=${tokenAsString}`
		);
	},
	getAllSurfaceColors(state: any) {
		if (state.powders.length <= 0) return [];
		// Get colors from powders
		let colors = state.powders
			.filter((it: PowderDto) => it.available)
			.map((it: PowderDto) => it.surfaceColor);
		// Remove duplicates
		colors = colors.filter(
			(it: SurfaceColorDto, index: any, self: any) =>
				self.findIndex((t: any) => t.id === it.id) === index
		);
		return colors;
	},
	articlesInCart(state: any) {
		return state.articles
			.filter((article: CustomerArticleDto) =>
				state.currentCart.cartItems.some(
					(item: CartItem) => item.articleId === article.articleId
				)
			)
			.sort((a: CustomerArticleDto, b: CustomerArticleDto) => {
				let aIndex = state.currentCart.cartItems.findIndex(
					(item: CartItem) => item.articleId === a.articleId
				);
				let bIndex = state.currentCart.cartItems.findIndex(
					(item: CartItem) => item.articleId === b.articleId
				);
				return aIndex - bIndex;
			});
	},
	availablePriceModels(state: any, getters: any) {
		if (getters.articlesInCart.length <= 0) {
			return [];
		}
		return getAllPriceModels().filter((p: any) =>
			getters.articlesInCart[0].priceLists.some(
				(pl: PriceListDto) => pl.priceModel == p.value
			)
		);
	},
	/**
	 * Gets a value indicating if there is more than one price model available in this
	 * region.
	 * This only works, if there are articles loaded.
	 * @param state current state of store
	 * @returns true, if there is at least one article with two price lists.
	 */
	hasPriceModels(state: any) {
		return (
			state.articles.find(
				(a: CustomerArticleDto) => a.priceLists && a.priceLists.length > 1
			) != undefined
		);
	},
	customerDeliveryAddressCount(state: any) {
		if (state.customer) {
			return state.customer.deliveryAddresses.length;
		}
	},
	customerInvoiceAddressCount(state: any) {
		if (state.customer) {
			return state.customer.invoiceAddresses.length;
		}
	},
	customerDefaultDeliveryAddress(state: any) {
		if (state.customer) {
			const address = state.customer.deliveryAddresses.filter((a: any) => a.isDefault)[0];
			return address ?? new AddressDto();
		} else {
			return new AddressDto();
		}
	},
	customerDefaultInvoiceAddress(state: any) {
		if (state.customer) {
			const address = state.customer.invoiceAddresses.filter((a: any) => a.isDefault)[0];
			return address ?? new AddressDto();
		} else {
			return new AddressDto();
		}
	},
	customerIsCountryValidForDelivery(state: any) {
		if (state.customer) {
			return state.customer.isCountryValidForDelivery;
		} else {
			console.log("customer is undefined");
			return false;
		}
	},
	userPreferredDeliveryAddress(state: any) {
		if (state.customer && state.customer.user.preferredDeliveryAddressId > 0) {
			const address = state.customer.deliveryAddresses.filter(
				(a: any) => a.id == state.customer.user.preferredDeliveryAddressId
			)[0];
			return address ?? new AddressDto();
		} else {
			return new AddressDto();
		}
	},
	userPreferredInvoiceAddress(state: any) {
		if (state.customer && state.customer.user.preferredInvoiceAddressId > 0) {
			const address = state.customer.invoiceAddresses.filter(
				(a: any) => a.id == state.customer.user.preferredInvoiceAddressId
			)[0];
			return address ?? new AddressDto();
		} else {
			return new AddressDto();
		}
	},
	isMaintenance(state: any) {
		const isMaintenance =
			state.bannerErrorMessage && state.bannerErrorMessage.startsWith("MAINTENANCE ");
		return isMaintenance;
	},
	calculatingArticleIds(state: any) {
		return state.articles
			.filter((article: CustomerArticleDto) => article.calculating)
			.map((article: CustomerArticleDto) => article.articleId);
	},
	getMaterial: (state: any) => (materialId: number) => {
		return state.werkstoffe
			.flatMap((w: WerkstoffDto) => w.materials)
			.find((m: MaterialDto) => m.id === materialId);
	},
	getWeldlineString: (state: any) => (weldlines: any) => {
		let text = "";

		if (weldlines.length === 1) {
			text = i18n.t("store.messages.weldlineOne") as string;
		} else if (weldlines.length > 1) {
			text = i18n.t("store.messages.weldlineMultiple", { count: weldlines.length }) as string;
		}
		const numberOfCorners = weldlines.reduce((a: any, b: any) => {
			return a + b.corners.length;
		}, 0);
		if (numberOfCorners === 1) {
			text += ` | ${i18n.t("store.messages.cornerOne") as string}`;
		} else if (numberOfCorners > 1) {
			text += ` | ${i18n.t("store.messages.cornerMultiple", { count: numberOfCorners }) as string}`;
		}
		if (weldlines.some((w: any) => w.clean)) {
			text += ` | ${i18n.t("store.messages.cleaned") as string}`;
		} else {
			text += ` | ${i18n.t("store.messages.notCleaned") as string}`;
		}
		return text;
	},
	getPowdercoatingFinishText: (state: any) => (value: number) => {
		switch (value) {
			case PowderFinish.Matt: {
				return i18n.t("store.messages.powderFinishMatt") as string;
			}
			case PowderFinish.Glossy: {
				return i18n.t("store.messages.powderFinishGlossy") as string;
			}
			default: {
				return i18n.t("store.messages.notAvailable") as string;
			}
		}
	},
	getPowdercoatingStructureText: (state: any) => (value: number) => {
		switch (value) {
			case PowderStructure.Smooth: {
				return i18n.t("store.messages.powderStructureSmooth") as string;
			}
			case PowderStructure.Fine: {
				return i18n.t("store.messages.powderStructureFine") as string;
			}
			case PowderStructure.Rough: {
				return i18n.t("store.messages.powderStructureRough") as string;
			}
			default: {
				return i18n.t("store.messages.notAvailable") as string;
			}
		}
	},
};
